// The Forgotten Workshop — small, honest bits of interactivity.
// No fake countdown timers, no fake "X people just bought this" popups —
// see the site's own "Story Shelf" section for why that matters to us.

document.addEventListener("DOMContentLoaded", () => {
  const topbar = document.querySelector(".topbar");
  const navToggle = document.querySelector(".nav-toggle");
  // #mainNav is the plain nav in the default header, or the two-group
  // wrapper in the centred-logo header — either way it's what collapses.
  const nav = document.getElementById("mainNav") || document.querySelector(".nav");
  const stickybar = document.querySelector(".stickybar");
  const hero = document.querySelector(".hero");

  // Sticky top bar shadow on scroll.
  // Guarded: a merchant can remove the header section, and an unguarded
  // dereference here would throw and take every feature below it with it.
  if (topbar) {
    const onScroll = () => {
      topbar.classList.toggle("is-scrolled", window.scrollY > 12);
    };
    document.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  // Mobile nav toggle
  if (navToggle && nav) {
    navToggle.addEventListener("click", () => {
      nav.classList.toggle("open");
      navToggle.setAttribute(
        "aria-expanded",
        nav.classList.contains("open") ? "true" : "false"
      );
    });
    nav.querySelectorAll("a").forEach((link) =>
      link.addEventListener("click", () => nav.classList.remove("open"))
    );
  }

  // Sticky buy bar — appears once the hero has scrolled past
  if (stickybar && hero) {
    const io = new IntersectionObserver(
      ([entry]) => {
        stickybar.classList.toggle("show", !entry.isIntersecting);
      },
      { threshold: 0 }
    );
    io.observe(hero);
  }

  // Tab groups — "What's Inside" (11 parts) and "Read a Chapter" (sample
  // chapters) both use the same [data-tabgroup] / [data-tab] / [data-tabpanel]
  // pattern, crossfading the incoming panel instead of an instant jump.
  document.querySelectorAll("[data-tabgroup]").forEach((group) => {
    const tabs = Array.from(group.querySelectorAll("[data-tab]"));
    const panels = Array.from(group.querySelectorAll("[data-tabpanel]"));

    const activate = (id, { scroll = false } = {}) => {
      tabs.forEach((tab) => {
        tab.setAttribute("aria-selected", tab.dataset.tab === id ? "true" : "false");
      });
      panels.forEach((panel) => {
        if (panel.id === id) {
          panel.classList.remove("tab-hidden");
          panel.classList.add("tab-fade-init");
          // Double rAF: let the browser paint the pre-transition state
          // first, or the opacity/translate change won't animate.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => panel.classList.remove("tab-fade-init"));
          });
        } else {
          panel.classList.add("tab-hidden");
        }
      });
      if (scroll) {
        group.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    };

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => activate(tab.dataset.tab));
    });
  });

  // Back to top
  const backToTop = document.getElementById("backToTop");
  if (backToTop) {
    const toggleBackToTop = () => {
      backToTop.classList.toggle("is-visible", window.scrollY > 700);
    };
    document.addEventListener("scroll", toggleBackToTop, { passive: true });
    toggleBackToTop();
    backToTop.addEventListener("click", () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }

  // The three editions are alternatives, not add-ons: the bundle contains the
  // other two. Only one may sit in the cart at a time.
  //
  // Enforced in two places. This first pass is the safety net — it runs on
  // every page load and reconciles whatever is actually in the cart, catching
  // additions the form handler below never saw: the Shop Pay and PayPal
  // express buttons, a restored cart from a previous visit, a second tab, or
  // a direct /cart/add link.
  (function enforceSingleEdition() {
    const ids = (window.EzraTheme || {}).exclusiveVariantIds || [];
    if (ids.length < 2) return; // nothing to clash with

    const exclusive = new Set(ids.map(String));

    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart || !cart.items) return;

        const matches = cart.items.filter((i) =>
          exclusive.has(String(i.variant_id))
        );
        if (matches.length < 2) return;

        // cart.items is in the order things were added, so the last match is
        // the edition the buyer chose most recently. That one wins.
        const keep = String(matches[matches.length - 1].variant_id);
        const updates = {};
        matches.forEach((i) => {
          const id = String(i.variant_id);
          if (id !== keep) updates[id] = 0;
        });

        return fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates: updates })
        }).then(() => {
          // The cart page is now showing lines that no longer exist.
          if (window.location.pathname.indexOf("/cart") === 0) {
            window.location.reload();
          }
        });
      })
      .catch(() => {
        /* A failed tidy-up must never block browsing or buying. */
      });
  })();

  // Second pass: clear the other two editions at the moment of purchase, so
  // the buyer never sees a cart or checkout containing duplicates. Any form
  // carrying data-remove-variants zeroes those lines before it submits.
  //
  // Note this runs on submit rather than on page load: the clash only matters
  // at the moment of purchase, and clearing the cart earlier would throw away
  // a selection the buyer might still change their mind about.
  document.querySelectorAll("form[action*='/cart/add']").forEach((form) => {
    const marker = form.querySelector("[data-remove-variants]");
    if (!marker) return;

    const ids = (marker.getAttribute("data-remove-variants") || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!ids.length) return;

    let clearing = false;
    form.addEventListener("submit", (e) => {
      if (clearing) return; // second pass, after the cart has been cleaned
      e.preventDefault();
      clearing = true;

      const updates = {};
      ids.forEach((id) => {
        updates[id] = 0;
      });

      const submitNow = () => form.submit();

      // Whatever happens, the purchase still goes through: a failed cleanup
      // is worth far less than a blocked checkout.
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates: updates })
      })
        .then(submitNow)
        .catch(submitNow);
    });
  });

  // Exit-intent bundle offer — shows up to TWICE per browser session. First
  // show is triggered by desktop exit-intent (cursor leaving toward the tab/
  // URL bar) or, on mobile, a fast upward swipe past the halfway point of
  // the page. The second show fires automatically 1.5 minutes after the
  // first one appeared — not re-triggered by exit-intent again, just a
  // straightforward reminder timer. The countdown survives a page reload:
  // the first-shown timestamp is persisted, so if 1.5 minutes have already
  // passed by the time the page reloads, the second show fires immediately
  // instead of being lost.
  const exitModal = document.getElementById("exitModal");
  const exitModalClose = document.getElementById("exitModalClose");
  if (exitModal && exitModalClose) {
    const cfg = window.EzraTheme || {};
    const COUNT_KEY = "forgottenWorkshopExitOfferCount";
    const FIRST_AT_KEY = "forgottenWorkshopExitOfferFirstAt";
    // The Exit offer section carries its own timing on data attributes; the
    // global settings remain as a fallback for older saved themes.
    const attr = (name, fallback) => {
      const raw = parseInt(exitModal.getAttribute(name), 10);
      return Number.isNaN(raw) ? fallback : raw;
    };
    const SECOND_SHOW_DELAY_MS =
      attr("data-repeat-delay", cfg.exitOfferDelaySeconds || 90) * 1000;
    const MAX_SHOWS = attr("data-max-shows", cfg.exitOfferMaxShows || 2);

    // Add ?popup to any URL to wipe the session counter and show the offer
    // straight away. Without this the offer is invisible while testing: it
    // caps at MAX_SHOWS per session, so by the second or third reload it has
    // legitimately used up its allowance and stays hidden.
    const forceExitOffer = /[?&]popup\b/.test(window.location.search);
    if (forceExitOffer) {
      try {
        sessionStorage.removeItem(COUNT_KEY);
        sessionStorage.removeItem(FIRST_AT_KEY);
      } catch (e) {
        /* see note below */
      }
    }

    let showCount = 0;
    let firstShownAt = null;
    try {
      showCount = parseInt(sessionStorage.getItem(COUNT_KEY) || "0", 10) || 0;
      const stored = sessionStorage.getItem(FIRST_AT_KEY);
      firstShownAt = stored ? parseInt(stored, 10) : null;
    } catch (e) {
      // Storage unavailable (private mode, etc.) — falls back to in-memory
      // only; the offer may repeat across page loads, a minor annoyance,
      // not a bug worth blocking the feature over.
    }

    const scheduleSecondShow = (delayMs) => {
      setTimeout(() => {
        if (showCount < MAX_SHOWS) openExitModal();
      }, Math.max(0, delayMs));
    };

    let dismissedUntil = 0;
    const openExitModal = () => {
      if (exitModal.classList.contains("is-open")) return;
      if (Date.now() < dismissedUntil) return;
      if (showCount >= MAX_SHOWS) return;
      showCount++;
      try {
        sessionStorage.setItem(COUNT_KEY, String(showCount));
      } catch (e) {
        /* see note above */
      }
      exitModal.classList.add("is-open");
      document.body.style.overflow = "hidden";
      if (showCount === 1) {
        firstShownAt = Date.now();
        try {
          sessionStorage.setItem(FIRST_AT_KEY, String(firstShownAt));
        } catch (e) {
          /* see note above */
        }
        scheduleSecondShow(SECOND_SHOW_DELAY_MS);
      }
    };
    const closeExitModal = () => {
      if (!exitModal.classList.contains("is-open")) return;
      exitModal.classList.remove("is-open");
      document.body.style.overflow = "";
      dismissedUntil = Date.now() + 2500;
    };

    // Picks the countdown back up after a reload, instead of losing it.
    if (showCount === 1 && firstShownAt) {
      scheduleSecondShow(SECOND_SHOW_DELAY_MS - (Date.now() - firstShownAt));
    }

    // Timed first appearance. Exit intent only fires for people who are
    // already leaving; this catches the ones still reading. Set the delay to
    // 0 in Theme settings to turn it off and rely on exit intent alone.
    const firstDelay = forceExitOffer ? 1 : attr("data-first-delay", cfg.exitOfferFirstDelaySeconds);
    if (firstDelay && firstDelay > 0 && showCount === 0) {
      setTimeout(() => {
        if (showCount === 0) openExitModal();
      }, firstDelay * 1000);
    }

    exitModalClose.addEventListener("click", closeExitModal);
    exitModal.addEventListener("click", (e) => {
      if (e.target === exitModal) closeExitModal();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && exitModal.classList.contains("is-open")) closeExitModal();
    });

    // Desktop: cursor exits through the top of the viewport.
    document.addEventListener("mouseout", (e) => {
      if (!e.relatedTarget && e.clientY <= 0) openExitModal();
    });

    // Mobile: no mouse to leave with, so watch for a fast upward swipe
    // after the visitor has already scrolled past the halfway point —
    // a reasonable proxy for "about to hit back."
    let lastY = window.scrollY;
    let lastT = performance.now();
    document.addEventListener(
      "scroll",
      () => {
        const y = window.scrollY;
        const t = performance.now();
        const dy = lastY - y; // positive = scrolling up
        const dt = Math.max(1, t - lastT);
        const pastHalfway = y > document.documentElement.scrollHeight * 0.5 - window.innerHeight;
        if (pastHalfway && dy > 60 && dt < 300) openExitModal();
        lastY = y;
        lastT = t;
      },
      { passive: true }
    );
  }

  // The workshop drawer — slides open to reveal what's inside.
  const drawers = document.querySelectorAll("[data-drawer]");
  const scrollDrawers = [];

  drawers.forEach((drawer) => {
    const toggle = drawer.querySelector("[data-drawer-toggle]");
    const hint = drawer.querySelector("[data-drawer-hint]");
    if (!toggle) return;

    const setHint = (isOpen) => {
      if (!hint) return;
      hint.textContent = isOpen
        ? drawer.getAttribute("data-hint-open") || ""
        : drawer.getAttribute("data-hint-closed") || "";
    };

    toggle.addEventListener("click", () => {
      // A click takes over from the scroll effect for the rest of the visit.
      drawer.classList.add("is-manual");
      const isOpen = drawer.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
      setHint(isOpen);
    });

    if (drawer.hasAttribute("data-drawer-scroll")) {
      scrollDrawers.push({ el: drawer, toggle: toggle, setHint: setHint, wasOpen: false });
    }
  });

  // Scroll-driven pull, V2.7.
  //
  // Three things changed here, and all three are why it now glides.
  //
  // 1. The cavity's real height is measured once into --cav-h, so the CSS
  //    can slide the contents with a transform instead of growing a
  //    max-height. No layout per frame.
  // 2. The raw scroll progress is eased with a smoothstep, so the drawer
  //    starts and finishes gently instead of moving fastest at the moment
  //    it begins.
  // 3. The value the drawer actually uses chases the eased target rather
  //    than snapping to it. That single line is the difference between
  //    tracking the wheel notch-by-notch and gliding.
  if (scrollDrawers.length) {
    var reduceMo = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var running = false;

    var measure = function () {
      scrollDrawers.forEach(function (d) {
        var inner = d.el.querySelector(".ezra-drawer-inner");
        var cav = d.el.querySelector(".ezra-drawer-cavity");
        if (!inner || !cav) return;
        // Read the natural height with the slide neutralised, or we would
        // measure the transformed position instead of the content.
        var prev = inner.style.transform;
        inner.style.transform = "none";
        var h = inner.offsetHeight;
        inner.style.transform = prev;
        if (h > 0) d.el.style.setProperty("--cav-h", h + "px");
      });
    };

    // 0 at the edges, 1 in the middle, with zero velocity at both ends.
    var smoothstep = function (t) { return t * t * (3 - 2 * t); };

    var frame = function () {
      running = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var moving = false;

      scrollDrawers.forEach(function (d) {
        if (d.el.classList.contains("is-manual")) return;

        var rect = d.el.getBoundingClientRect();
        var start = vh * 0.92;
        var distance = vh * 0.46;
        var raw = (start - rect.top) / distance;
        raw = Math.max(0, Math.min(1, raw));
        var target = smoothstep(raw);

        if (d.cur === undefined) d.cur = target;
        // The damping. 0.16 is slow enough to read as weight and fast
        // enough that it never feels like it is lagging behind you.
        d.cur = reduceMo ? target : d.cur + (target - d.cur) * 0.16;
        if (Math.abs(target - d.cur) > 0.0015) moving = true;
        else d.cur = target;

        d.el.style.setProperty("--pull", d.cur.toFixed(4));

        var isOpen = d.cur > 0.5;
        if (isOpen !== d.wasOpen) {
          d.wasOpen = isOpen;
          d.toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
          d.setHint(isOpen);
        }
      });

      // Keep the loop alive while anything is still settling, so the drawer
      // finishes its travel after you stop scrolling instead of freezing.
      if (moving) { running = true; requestAnimationFrame(frame); }
    };

    const onScrollOrResize = () => {
      if (running) return;
      running = true;
      requestAnimationFrame(frame);
    };

    document.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", function () { measure(); onScrollOrResize(); });
    measure();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { measure(); onScrollOrResize(); });
    window.addEventListener("load", function () { measure(); onScrollOrResize(); });
    frame();
  }

  // Gallery lightbox. One overlay is built lazily and shared by every
  // gallery on the page.
  const galleries = document.querySelectorAll("[data-gallery]");
  if (galleries.length) {
    let lightbox = null;
    let items = [];
    let index = 0;

    const build = () => {
      lightbox = document.createElement("div");
      lightbox.className = "gallery-lightbox";
      lightbox.setAttribute("role", "dialog");
      lightbox.setAttribute("aria-modal", "true");
      lightbox.innerHTML =
        '<button type="button" class="gallery-lb-btn gallery-lb-close" aria-label="Close">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg></button>' +
        '<button type="button" class="gallery-lb-btn gallery-lb-prev" aria-label="Previous image">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg></button>' +
        '<img alt="">' +
        '<button type="button" class="gallery-lb-btn gallery-lb-next" aria-label="Next image">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg></button>' +
        '<p class="gallery-lightbox-caption"></p>';
      document.body.appendChild(lightbox);

      lightbox.querySelector(".gallery-lb-close").addEventListener("click", close);
      lightbox.querySelector(".gallery-lb-prev").addEventListener("click", () => step(-1));
      lightbox.querySelector(".gallery-lb-next").addEventListener("click", () => step(1));
      lightbox.addEventListener("click", (e) => {
        if (e.target === lightbox) close();
      });
    };

    const show = () => {
      const item = items[index];
      if (!item) return;
      lightbox.querySelector("img").src = item.getAttribute("data-full");
      lightbox.querySelector("img").alt = item.getAttribute("data-caption") || "";
      lightbox.querySelector(".gallery-lightbox-caption").textContent =
        item.getAttribute("data-caption") || "";
    };

    const step = (dir) => {
      index = (index + dir + items.length) % items.length;
      show();
    };

    function close() {
      if (!lightbox) return;
      lightbox.classList.remove("is-open");
      document.body.style.overflow = "";
      const opener = items[index];
      if (opener) opener.focus();
    }

    galleries.forEach((gallery) => {
      const galleryItems = Array.from(gallery.querySelectorAll("[data-gallery-item]"));
      galleryItems.forEach((item, i) => {
        item.addEventListener("click", () => {
          if (!lightbox) build();
          items = galleryItems;
          index = i;
          show();
          lightbox.classList.add("is-open");
          // Stop the page scrolling behind the overlay.
          document.body.style.overflow = "hidden";
          lightbox.querySelector(".gallery-lb-close").focus();
        });
      });
    });

    document.addEventListener("keydown", (e) => {
      if (!lightbox || !lightbox.classList.contains("is-open")) return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
    });
  }

  // Scrollable chapter panels: drop the bottom fade once the reader has
  // reached the end, so it doesn't sit over the last line of text.
  document.querySelectorAll("[data-chapter-scroll]").forEach((panel) => {
    const wrap = panel.closest(".sample-chapter-wrap");
    if (!wrap) return;

    const update = () => {
      const atEnd = panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 8;
      const noScroll = panel.scrollHeight <= panel.clientHeight + 1;
      wrap.classList.toggle("is-at-end", atEnd || noScroll);
    };

    panel.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    update();
  });

  // Product gallery — swap the main image when a thumbnail is chosen.
  const productPhoto = document.getElementById("ProductPhoto");
  const thumbs = document.querySelectorAll("[data-product-thumb]");
  if (productPhoto && thumbs.length) {
    thumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => {
        productPhoto.src = thumb.getAttribute("data-full");
        thumbs.forEach((t) => t.setAttribute("aria-current", t === thumb ? "true" : "false"));
      });
    });
  }

  // Product page — keep the displayed price in step with the chosen variant.
  const variantSelect = document.getElementById("ProductSelect");
  const priceEl = document.getElementById("ProductPrice");
  if (variantSelect && priceEl) {
    variantSelect.addEventListener("change", () => {
      const opt = variantSelect.options[variantSelect.selectedIndex];
      const price = opt && opt.getAttribute("data-price");
      if (price) priceEl.textContent = price;
      // A variant switch can invalidate the compare-at price, so drop it
      // rather than leave a stale strike-through next to a new price.
      const compareEl = document.getElementById("ProductComparePrice");
      if (compareEl) compareEl.remove();
    });
  }

  // Gentle reveal-on-scroll
  const revealEls = document.querySelectorAll(".reveal");
  if ("IntersectionObserver" in window && revealEls.length) {
    const revealIO = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );
    revealEls.forEach((el) => revealIO.observe(el));
  } else {
    revealEls.forEach((el) => el.classList.add("in"));
  }
});


/* =====================================================================
   EZRA V2.7 — two additions. Both are progressive: if the markup they
   look for is not on the page, nothing happens and nothing breaks.
   ===================================================================== */
(function () {
  "use strict";
  var reduceMo = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---- A. The part navigation, made fluent ------------------------
     The pills already worked; what was missing was continuity. An
     indicator now travels between them instead of the highlight
     teleporting, and the incoming panel is measured so it cross-fades
     without the page jumping. 260ms ease-in-out is the curve for
     something already on screen changing position, as opposed to the
     ease-out used for things entering. Markup is untouched — the
     indicator is inserted here, so your Liquid stays as it is. */
  document.querySelectorAll(".tabbar").forEach(function (bar) {
    if (bar.querySelector(".tabbar-ink")) return;
    var pills = Array.prototype.slice.call(bar.querySelectorAll(".tab-pill"));
    if (pills.length < 2) return;

    var ink = document.createElement("span");
    ink.className = "tabbar-ink";
    ink.setAttribute("aria-hidden", "true");
    bar.appendChild(ink);
    bar.classList.add("tabbar--inked");

    function place(el, animate) {
      if (!el) return;
      var b = bar.getBoundingClientRect(), r = el.getBoundingClientRect();
      ink.style.transition = animate && !reduceMo
        ? "transform .26s cubic-bezier(.77,0,.175,1), width .26s cubic-bezier(.77,0,.175,1), height .26s cubic-bezier(.77,0,.175,1)"
        : "none";
      ink.style.width = r.width + "px";
      ink.style.height = r.height + "px";
      ink.style.transform = "translate3d(" + (r.left - b.left) + "px," + (r.top - b.top) + "px,0)";
    }
    function current() {
      return bar.querySelector('.tab-pill[aria-selected="true"]')
          || bar.querySelector(".tab-pill.is-active")
          || pills[0];
    }
    pills.forEach(function (p) {
      p.addEventListener("click", function () { requestAnimationFrame(function () { place(current(), true); }); });
      p.addEventListener("keyup", function () { requestAnimationFrame(function () { place(current(), true); }); });
    });
    var settle = function () { place(current(), false); };
    settle();
    window.addEventListener("resize", settle);
    window.addEventListener("load", settle);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(settle);
    // The active pill can also change from code elsewhere in the theme.
    new MutationObserver(function () { place(current(), true); })
      .observe(bar, { attributes: true, subtree: true, attributeFilter: ["aria-selected", "class"] });
  });

  /* ---- B. The cover floats -----------------------------------------
     A slow, damped rise as the offer band comes up the viewport. Small
     on purpose: the book should feel like it is sitting in the room,
     not bobbing. Transform only, and switched off for reduced motion. */
  var cover = document.querySelector(".price-cover");
  if (cover && !reduceMo) {
    var cur = 0, running = false;
    function tick() {
      running = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var r = cover.getBoundingClientRect();
      var target = Math.max(0, Math.min(1, (vh * 0.95 - r.top) / (vh * 0.7)));
      cur += (target - cur) * 0.12;
      cover.style.setProperty("--float", cur.toFixed(4));
      if (Math.abs(target - cur) > 0.002) { running = true; requestAnimationFrame(tick); }
    }
    var go = function () { if (!running) { running = true; requestAnimationFrame(tick); } };
    document.addEventListener("scroll", go, { passive: true });
    window.addEventListener("resize", go);
    go();
  }
})();


/* =====================================================================
   EZRA V2.8 — pointer tilt + specular on the book covers.
   Wraps the existing <img> at runtime, so no Liquid changes: the offer
   stack cover and the pricing cover both get it, and anything else you
   add later only needs the class.
   ===================================================================== */
(function () {
  "use strict";
  if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  var targets = [];
  document.querySelectorAll(".offer-cover img, img.price-cover").forEach(function (img) {
    targets.push(img);
  });
  if (!targets.length) return;

  targets.forEach(function (img) {
    if (img.closest(".tilt3d")) return;

    var outer = document.createElement("span");
    outer.className = "tilt3d";
    var inner = document.createElement("span");
    inner.className = "tilt3d-inner";
    var shine = document.createElement("span");
    shine.className = "tilt3d-shine";
    shine.setAttribute("aria-hidden", "true");

    img.parentNode.insertBefore(outer, img);
    outer.appendChild(inner);
    inner.appendChild(img);
    inner.appendChild(shine);

    // Targets and current values, so the tilt eases in and — more
    // importantly — eases back to flat on leave instead of snapping.
    var tx = 0, ty = 0, tl = 0;
    var cx = 0, cy = 0, cl = 0;
    var raf = 0;

    function loop() {
      cx += (tx - cx) * 0.14;
      cy += (ty - cy) * 0.14;
      cl += (tl - cl) * 0.14;
      inner.style.setProperty("--rx", cx.toFixed(4));
      inner.style.setProperty("--ry", cy.toFixed(4));
      shine.style.setProperty("--rx", cx.toFixed(4));
      shine.style.setProperty("--lit", cl.toFixed(4));
      if (Math.abs(tx - cx) > 0.001 || Math.abs(ty - cy) > 0.001 || Math.abs(tl - cl) > 0.001) {
        raf = requestAnimationFrame(loop);
      } else {
        raf = 0;
      }
    }
    function kick() { if (!raf) raf = requestAnimationFrame(loop); }

    outer.addEventListener("pointermove", function (e) {
      var r = inner.getBoundingClientRect();
      if (!r.width || !r.height) return;
      // -0.5 at one edge, +0.5 at the other.
      tx = (e.clientX - (r.left + r.width / 2)) / r.width;
      ty = (e.clientY - (r.top + r.height / 2)) / r.height;
      tl = 1;
      kick();
    });
    outer.addEventListener("pointerleave", function () {
      tx = 0; ty = 0; tl = 0;
      kick();
    });
  });
})();
